import {useCallback, useEffect, useState} from "react"

import { HashRouter as Router, Switch, useRouteMatch } from "react-router-dom"

import { StaffRoute } from "@meddbriefer/mdb-auth"

import { MDB_TYPES } from "@meddbriefer/scenario-data/constants"

import { useScenarioType } from "../ScenarioTypeContext"

import { CheckListContext } from "./CheckListContext"


import Phase from "./Phase"
import PhaseList from "./PhaseList"
import SubPhase from "./SubPhase"
import ChangesModal from "./ChangesModal"


const CONTAINER_TYPES = [MDB_TYPES.PHASE, MDB_TYPES.SUB_PHASE, MDB_TYPES.NESTED_CHECKLIST]

// takes a list of objects and fieldName (key) and returns an object who's attributes are
// the distinct values for that field mapped to a list objects which share that
// value
const groupByKey = (list, key) => list.reduce(
    (hash, obj) => ({ ...hash, [obj[key]]: (hash[obj[key]] || []).concat(obj) }),
    {}
)

const genPrevBlock = (array, index) => {
    if (index === 0) return null
    const prevItem = array[index - 1]
    return {
        index: prevItem.index,
        lastIndex: prevItem.lastIndex
    }
}
const genNextBlock = (array, index) => {
    if (index === array.length - 1) return null
    const nextItem = array[index + 1]
    return {
        index: nextItem.index,
        lastIndex: nextItem.lastIndex
    }
}

const recursivelyIndexItem = (item) => {
    if ("items" in item) {
        // embedded checklist
        item.items = item.items.map(child => recursivelyIndexItem(child))
        item.lastIndex = item.items[item.items.length - 1].lastIndex
    } else if ("options" in item) {
        // decision or assessment-with-options
        item.options = item.options.map(child => recursivelyIndexItem(child))
        item.lastIndex = item.options[item.options.length - 1].lastIndex
    } else {
        // simple checklist item type (no children such as items or options)
        item.lastIndex = item.index
    }
    return item
}

const recursivelyComputeBlocks = (item, i, arr) => {
    if ("items" in item) {
        item.items = item.items.map((sub, i, arr) => recursivelyComputeBlocks(sub, i, arr))
    } else if ("options" in item) {
        item.options = item.options.map((opt, i, arr) => recursivelyComputeBlocks(opt, i, arr))
    }
    item.prevBlock = genPrevBlock(arr, i)
    item.nextBlock = genNextBlock(arr, i)
    return item
}

const CheckListEditor = () => {
    // const [rawData, setRawData] = useState(null)
    const {scenarioType} = useScenarioType()
    const { path } = useRouteMatch()
    // the data which we'll be working on is contained within scenarioType.checklist
    const [data, setData] = useState(scenarioType.checklist)
    const [uniqueIDs, setUniqueIDs] = useState(null)
    const [uniqueLabels, setUniqueLabels] = useState(null)
    const [modifiedFlds, setModifiedFlds] = useState({})
    // set to true initially, so that useEffect rebuilds the hierarchy
    const [hierUpdated, setHierUpdated] = useState(true)
    const [labelsUpdated, setLabelsUpdated] = useState(true)
    // const [parentIDs, setParentIds] = useState(null)
    // const [hierarchy, setHierarchy] = useState(null)
    // const [hierarchyCreated, setHierarchyCreated] = useState(false)
    const [hier, setHier] = useState(null)
    const [modalIsShown, setShowModal] = useState(false)

    const hideModal = () => setShowModal(false)
    const showModal = () => setShowModal(true)

    const genID = (rec, fldName) => `row-${rec._ctx.index}-${fldName}`

    const getRec = (ctx) => {
        return Object.assign({}, data[ctx.index], {_ctx: ctx})
    }

    const insertRec = (rec, index) => {
        const copy = [...data]
        copy.splice(index, 0, rec)
        // console.log(copy)
        if (!!modifiedFlds[rec.id] && "deleted" === modifiedFlds[rec.id].status) {
            setModifiedFlds(prevState => {
                const dup = Object.assign({}, prevState)
                delete dup[rec.id]
                return dup
            })
        } else {
            setModifiedFlds(prevState => {
                const newRec = Object.assign({}, rec, { status: "added" })
                return Object.assign({}, prevState, {[rec.id]: newRec})
            })
        }
        setData(copy)
        setHierUpdated(true)
        setLabelsUpdated(true)
    }

    const updateRec = (rec) => {
        const index = rec._ctx.index
        delete rec._ctx
        setData([
            ...data.slice(0, index),
            rec,
            ...data.slice(index+1)
        ])
    }

    const deleteRec = (rec) => {
        // rec may have children, so we need to look at index and lastIndex to determine how many to delete
        const copy = [...data]
        const start = rec._ctx.index
        const numDelete = (rec._ctx.lastIndex - start) + 1
        const deleted = copy.splice(start, numDelete)
        deleted.forEach(item => {
            if (!!modifiedFlds[item.id] && "added" === modifiedFlds[item.id].status) {
                // don't report a recently added (since last save) field as deleted
                setModifiedFlds(prevState => {
                    const dup = Object.assign({}, prevState)
                    delete dup[item.id]
                    return dup
                })
            } else {
                const newVal = Object.assign({}, item, {status: "deleted"})
                setModifiedFlds(prevState => Object.assign({}, prevState, {[item.id]: newVal}))
            }
        })
        setData(copy)
        setHierUpdated(true)
        setLabelsUpdated(true)
    }

    const moveUp = (init1stBlock, init2ndBlock) => {
        // will delete the 2nd block, and re-insert it before 1st block
        // each block can simply be of length 1 (lastIndex is undefined)
        const copy = [...data]
        const startDelete = init2ndBlock.index
        const numDelete = (init2ndBlock.lastIndex) ? init2ndBlock.lastIndex - (startDelete -1) : 1
        const deleted = copy.splice(startDelete, numDelete)
        copy.splice(init1stBlock.index, 0, ...deleted)
        setData(copy)
        setHierUpdated(true)
    }

    const moveDown = (init1stBlock, init2ndBlock) => {
        // will delete the 1st block, and re-insert it after the 2nd block
        // each block can simply be of length 1 (lastIndex is undefined)
        const copy = [...data]
        const startDelete = init1stBlock.index
        const numDelete = (init1stBlock.lastIndex) ? init1stBlock.lastIndex - (startDelete -1) : 1
        const reinsertAt = (init2ndBlock.lastIndex) ? init2ndBlock.lastIndex - (numDelete -1) : init2ndBlock.index - (numDelete -1)
        const deleted = copy.splice(startDelete, numDelete)
        copy.splice(reinsertAt, 0, ...deleted)
        setData(copy)
        setHierUpdated(true)
    }

    const getAdded = () => Object.values(modifiedFlds).filter(rec => rec.status === "added")

    const getRemoved = () => Object.values(modifiedFlds).filter(rec => rec.status === "deleted")

    const calcHierarchy1 = useCallback(
        () => {
            const parent2Children = {}

            const recursivelyExpandChild = (child) => {
                if (child.index in parent2Children) {
                    const childRec = parent2Children[child.index]
                    delete parent2Children[child.index]
                    if (childRec.items) {
                        childRec.items.forEach((item, i) => {
                            childRec.items[i] = recursivelyExpandChild(item)
                        })
                    }
                    return childRec
                }
                return child
            }

            const copy = data.map((rec, i) => Object.assign({}, rec, {index: i}))
            // find records with a parentID property, and group them, mapping them to
            // parent2Children id => list
            const recsWithParents = copy.filter(rec => "parentID" in rec)
            for (const [parentID, children] of Object.entries(groupByKey(recsWithParents, "parentID"))) {
                const parent = copy.find(rec => rec.id === parentID)
                const newRec = { id: parent.id, type: parent.type, index: parent.index }
                const newChildren = children.map(child => { return {id: child.id, index: child.index, type: child.type } })

                switch (parent.type) {
                    case MDB_TYPES.PHASE:
                        if (newChildren[0].type === MDB_TYPES.SUB_PHASE) {
                            newRec.subPhases = newChildren
                        } else {
                            newRec.items = newChildren
                        }
                        parent2Children[parent.index] = newRec
                        break
                    case MDB_TYPES.SUB_PHASE:
                        newRec.items = newChildren
                        parent2Children[parent.index] = newRec
                        break
                    case MDB_TYPES.NESTED_CHECKLIST:
                        newRec.items = newChildren
                        parent2Children[parent.index] = newRec
                        break
                    case MDB_TYPES.DECISION:
                    case MDB_TYPES.AWO:
                        newRec.options = newChildren
                        parent2Children[parent.index] = newRec
                        break
                    default:
                        console.log(`unexpected parent type: ${parent.type} childtype: ${children[0].type}`)
                }
            }
            // add a new record to parent2children for newly added container types, so that they will get added
            // to the hierarchy
            const containers = copy.filter(rec => CONTAINER_TYPES.includes(rec.type))
            containers.forEach(rec => {
                if (! (rec.index in parent2Children)) {
                    parent2Children[rec.index] = rec
                }
            })

            const phases = Object.entries(parent2Children).filter(([k, v], idx) => v.type === MDB_TYPES.PHASE).map(([k, v]) => v)
            // start with phases and recursively add children, from parent2Children, removing the parent2children records afterwards.
            // at the end parent2Children should only contain the phases
            phases.forEach((phase, i) => {
                phase.items?.forEach((item, i) => {
                    phase.items[i] = recursivelyExpandChild(item)
                })
                phase.subPhases?.forEach((sp, i) => {
                    phase.subPhases[i] = recursivelyExpandChild(sp)
                })
            })

            // compute lastIndices attribute for the entire tree
            phases.forEach((phase, i) => {
                if (phase.items) {
                    phase.items.map((item) => recursivelyIndexItem(item))
                    phase.lastIndex = phase.items[phase.items.length -1].lastIndex
                } else if (phase.subPhases) {
                    phase.subPhases = phase.subPhases.map(sp => {
                        if (sp.items) {
                            sp.items = sp.items.map(item => recursivelyIndexItem(item))
                            sp.lastIndex = sp.items[sp.items.length-1].lastIndex
                        } else {
                            sp.lastIndex = sp.index
                        }
                        return sp
                    })
                    phase.lastIndex = phase.subPhases[phase.subPhases.length-1].lastIndex
                } else {
                    // for either new phases or phases which just had all children deleted
                    phase.lastIndex = phase.index
                }
            })

            // compute next/prev blocks for the entire tree
            phases.forEach((phase, i, arr) => {
                if (phase.items) {
                    phase.items.map((item, _i, _arr) => recursivelyComputeBlocks(item, _i, _arr))
                } else if (phase.subPhases) {
                    phase.subPhases = phase.subPhases.map((sp, _i, _arr) => {
                        if (sp.items) {
                            sp.items = sp.items.map((item, j, arr2) => recursivelyComputeBlocks(item, j, arr2))
                        }
                        sp.prevBlock = genPrevBlock(_arr, _i)
                        sp.nextBlock = genNextBlock(_arr, _i)
                        return sp
                    })
                }
                phase.prevBlock = genPrevBlock(arr, i)
                phase.nextBlock = genNextBlock(arr, i)
            })
            return phases

        }, [data]
    )

    useEffect(
        () => {
            if (data) {
                if (hierUpdated) {
                    setHier(calcHierarchy1())
                    // Id's have been already pre-checked to be unique and this
                    // state var is used to make sure new ones are also unique
                    setUniqueIDs(data.map(rec => rec.id))
                    setHierUpdated(false)
                }
                if (labelsUpdated) {
                    setUniqueLabels(data.map(rec => rec.label))
                    setLabelsUpdated(false)
                }
            }
        }, [data, hierUpdated, labelsUpdated, calcHierarchy1]
    )

    const getType = (rec) => {
        const type = rec.type
        let retVal = type
        switch (type) {
            case "phase":
                retVal = "Section"
                break
            case "sub-phase":
                retVal = "Sub-Section"
                break
            default:
                break
        }
        return retVal
    }


    if (!hier) {
        return (<div>loading...</div>)
    }

    return (
        <div>
            <Router>
                <CheckListContext.Provider
                    value={{
                        hier,
                        uniqueIDs,
                        uniqueLabels,
                        setLabelsUpdated,
                        data,
                        getRec,
                        insertRec,
                        updateRec,
                        deleteRec,
                        setData,
                        genID,
                        moveUp,
                        moveDown,
                        showModal,
                        hideModal,
                        getType
                    }}
                >
                <div className="checklist-editor">
                    <Switch>
                        <StaffRoute path={`${path}/phase/:phaseIdx/subphase/:subPhaseIdx`} component={SubPhase} />
                        <StaffRoute path={`${path}/phase/:phaseIdx`} component={Phase} />
                        <StaffRoute path="/scenario-type/:scenarioTypeID/checklist" component={PhaseList} />
                    </Switch>

                    <ChangesModal
                        additions={getAdded()}
                        deletions={getRemoved()}
                        isOpen={modalIsShown}
                    />
                </div>
                </CheckListContext.Provider>
            </Router>
        </div>
    )
}

export default CheckListEditor